import React from 'react';
import { X, Scale } from 'lucide-react';

interface TermsOfUseModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TermsOfUseModal: React.FC<TermsOfUseModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
      
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="relative w-full max-w-4xl bg-white rounded-xl shadow-2xl">
          <div className="flex items-center justify-between p-6 border-b">
            <div className="flex items-center space-x-3">
              <Scale className="h-6 w-6 text-cyan-600" />
              <h2 className="text-2xl font-semibold text-gray-900">Terms of Use</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition"
            >
              <X className="h-6 w-6 text-gray-500" />
            </button>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(100vh-200px)]">
            <div className="prose max-w-none">
              <p className="text-sm text-gray-500 mb-6">Effective Date: 1 February 2025</p>

              <section className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">1. Introduction</h3>
                <p className="text-gray-600">
                  Welcome to Prestige AI, a platform providing AI-driven solutions for auditors, financial professionals, and other users. By accessing and using our web application and AI bots (collectively, the "Services"), you agree to the following Terms of Use. If you do not agree with these terms, please do not use our Services.
                </p>
              </section>

              <section className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">2. Nature of AI-Generated Content</h3>
                <p className="text-gray-600">
                  Our AI-driven bots provide automated analysis, document review, financial statement checks, and other services to assist users in auditing, financial reporting, and other professional tasks. However, all outputs generated by our Services should be carefully reviewed by qualified professionals before reliance or use.
                </p>
              </section>

              <section className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">3. No Guarantee of Accuracy or Completeness</h3>
                <p className="text-gray-600 mb-4">While we strive to provide accurate and useful AI-generated results, Prestige AI does not guarantee:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>The completeness, accuracy, or reliability of AI-generated reports, reviews, or analyses.</li>
                  <li>That AI-generated content will comply with legal, regulatory, or professional standards.</li>
                  <li>That AI-generated financial calculations and assessments will be free of errors.</li>
                </ul>
                <p className="text-gray-600 mt-4">Users are responsible for verifying all AI-generated outputs and should not rely solely on our Services for critical decision-making.</p>
              </section>

              <section className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">4. Limitation of Liability</h3>
                <p className="text-gray-600 mb-4">To the fullest extent permitted by law:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>Prestige AI, its affiliates, and its developers shall not be liable for any errors, omissions, or inaccuracies in the AI-generated content.</li>
                  <li>Prestige AI shall not be responsible for any decisions made based on AI-generated outputs, including but not limited to financial, legal, or audit-related decisions.</li>
                  <li>Users acknowledge and accept that AI models may introduce biases, errors, or limitations, and that professional judgment is required in interpreting AI-generated results.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">5. Regulatory Considerations</h3>
                <p className="text-gray-600 mb-4">Users must comply with all applicable laws, regulations, and professional standards when using AI-generated outputs. Global Audit Regulators have identified key risks in AI-assisted audits, including:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>Fabrication of Audit Evidence: AI-generated content may inadvertently create misleading information that requires independent verification.</li>
                  <li>Data Confidentiality: Users should ensure AI tools do not expose confidential client or company information.</li>
                  <li>Over-Reliance on AI: AI outputs should be supplemented with human expertise and skepticism.</li>
                  <li>Lack of Transparency: AI models may operate as "black boxes," requiring additional validation and documentation to ensure audit trail integrity.</li>
                </ul>
                <p className="text-gray-600 mt-4">Users should independently verify AI-generated financial analysis, working papers, and audit conclusions before reliance.</p>
              </section>

              <section className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">6. Acceptable Use</h3>
                <p className="text-gray-600">Users must not:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>Use AI-generated content to misrepresent financial statements, audit findings, or regulatory compliance.</li>
                  <li>Use our Services for fraudulent, illegal, or unethical purposes.</li>
                  <li>Share confidential or sensitive client information with AI systems without proper safeguards.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">7. Intellectual Property</h3>
                <p className="text-gray-600">
                  All AI-generated content remains the intellectual property of Prestige AI. Users are granted a limited, non-exclusive right to use the content for professional and internal purposes. Unauthorized distribution, reproduction, or modification of Prestige AI outputs is prohibited.
                </p>
              </section>

              <section className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">8. Privacy and Data Protection</h3>
                <p className="text-gray-600">
                  By using our Services, you agree that Prestige AI may process and store user data in compliance with applicable privacy laws. We take reasonable measures to ensure data security, but users are responsible for safeguarding their confidential information when using AI tools.
                </p>
              </section>

              <section className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">9. Disclaimer of Warranties</h3>
                <p className="text-gray-600">
                  Our Services are provided "as is" and "as available" without warranties of any kind, whether express or implied. Prestige AI disclaims all warranties related to AI-generated content, including its fitness for a particular purpose, accuracy, or compliance with regulatory standards.
                </p>
              </section>

              <section className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">10. Modification of Terms</h3>
                <p className="text-gray-600">
                  Prestige AI reserves the right to update or modify these Terms of Use at any time. Continued use of the Services constitutes acceptance of the modified terms.
                </p>
              </section>

              <section className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">11. Governing Law and Jurisdiction</h3>
                <p className="text-gray-600">
                  These Terms shall be governed by and construed in accordance with the laws of South Africa. Any disputes arising under these Terms shall be resolved in the courts of South Africa.
                </p>
              </section>

              <section className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">12. Contact Information</h3>
                <p className="text-gray-600">
                  For any questions regarding these Terms of Use, please contact us at{' '}
                  <a href="mailto:support@prestigeai.tech" className="text-cyan-600 hover:text-cyan-700">
                    support@prestigeai.tech
                  </a>.
                </p>
              </section>

              <div className="bg-gray-50 p-6 rounded-lg mt-8">
                <p className="text-gray-600">
                  By using Prestige AI, you acknowledge that AI-generated content carries inherent risks and limitations, and you agree to assume full responsibility for verifying and validating AI outputs before use.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUseModal;